import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, InputGroup, Modal, Row, Spinner, Table } from 'react-bootstrap';
import PageHeader from '../components/PageHeader';
import { adminApi, extractError } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';

/**
 * /my-subscription — Campaign Manager billing self-service portal.
 *
 * Sections:
 *   • Subscription summary card (plan, price, status, next renewal, cancel flag)
 *   • Stripe Customer Portal deep-link button (manage payment method / cancel)
 *   • Candidate assignment requests (list mine + submit new)
 */
export default function MySubscriptionPage() {
  const { user } = useAuth();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [sub, setSub] = useState(null);
  const [hasStripeCustomer, setHasStripeCustomer] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await adminApi.get('/me/subscription');
      setSub(r.data?.data || null);
      setHasStripeCustomer(!!r.data?.has_stripe_customer);
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const displayName = (user?.first_name || user?.full_name || user?.email || '').split(' ')[0];

  return (
    <>
      <PageHeader
        title={`My Subscription${displayName ? ` — ${displayName}` : ''}`}
        subtitle="View your plan, manage billing, and request candidate assignments."
      />
      <div className="page-content" data-testid="my-subscription-page">
        {error && <Alert variant="danger" data-testid="my-sub-error">{error}</Alert>}
        {loading ? (
          <div className="text-center py-5"><Spinner animation="border" /></div>
        ) : (
          <>
            <SubscriptionCard sub={sub} hasStripeCustomer={hasStripeCustomer} toast={toast} onRefresh={load} />
            <AssignmentRequestsCard toast={toast} />
          </>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Subscription card
// ---------------------------------------------------------------------------
function SubscriptionCard({ sub, hasStripeCustomer, toast, onRefresh }) {
  const [portalLoading, setPortalLoading] = useState(false);

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const returnUrl = `${window.location.origin}/my-subscription`;
      const r = await adminApi.post('/me/billing-portal', { return_url: returnUrl });
      if (r.data?.url) {
        window.location.href = r.data.url;
      } else {
        toast.error('Could not open billing portal.');
      }
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setPortalLoading(false);
    }
  };

  if (!sub) {
    return (
      <Card className="mb-4" data-testid="no-subscription-card">
        <Card.Body className="text-center py-5">
          <div style={{ fontSize: '2.5rem', color: '#adb5bd', marginBottom: '0.5rem' }}>
            <i className="bi bi-credit-card" />
          </div>
          <h5 className="mb-2">No active subscription</h5>
          <p className="text-muted mb-3">
            You don&apos;t have a subscription on file yet. Head to the subscribe page to pick a plan and start managing candidate profiles.
          </p>
          <Button variant="primary" href="/subscribe" data-testid="go-subscribe-btn">
            <i className="bi bi-arrow-right-circle me-1" />
            View plans
          </Button>
        </Card.Body>
      </Card>
    );
  }

  const status = sub.status || 'unknown';
  const statusVariant = status === 'active' ? 'success'
    : status === 'trialing' ? 'info'
    : status === 'past_due' ? 'warning'
    : status === 'canceled' ? 'dark'
    : 'secondary';

  const periodEnd = sub.current_period_end ? new Date(sub.current_period_end) : null;
  const periodEndStr = periodEnd
    ? periodEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '—';
  // Same DB field carries two meanings depending on whether the sub is
  // set to cancel — surface both semantics clearly to the user.
  const willCancel = !!sub.cancel_at_period_end;
  const periodEndLabel = willCancel ? 'Access ends on' : 'Next renewal';

  const price = sub.plan_effective_price != null ? `$${Number(sub.plan_effective_price).toFixed(2)}` : '—';
  const interval = sub.billing_interval ? `/${sub.billing_interval}` : '';

  return (
    <Card className="mb-4" data-testid="subscription-card">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <div>
          <div className="fw-semibold" data-testid="sub-plan-name">
            {sub.plan_name || 'Subscription'}
          </div>
          {sub.plan_description && (
            <small className="text-muted">{sub.plan_description}</small>
          )}
        </div>
        <Badge bg={statusVariant} data-testid="sub-status-badge">
          {status.toUpperCase()}
        </Badge>
      </Card.Header>
      <Card.Body>
        <Row className="g-3">
          <Col md={4}>
            <div className="text-muted small text-uppercase" style={{ letterSpacing: '0.04em' }}>Price</div>
            <div className="fs-4 fw-semibold" data-testid="sub-price">{price}<small className="text-muted fw-normal">{interval}</small></div>
          </Col>
          <Col md={4}>
            <div className="text-muted small text-uppercase" style={{ letterSpacing: '0.04em' }} data-testid="sub-period-label">{periodEndLabel}</div>
            <div className="fs-5" data-testid="sub-period-end">{periodEndStr}</div>
            {willCancel && (
              <Badge bg="warning" text="dark" className="mt-1" data-testid="sub-cancels-badge">
                Auto-renew off
              </Badge>
            )}
          </Col>
          <Col md={4}>
            <div className="text-muted small text-uppercase" style={{ letterSpacing: '0.04em' }}>Last payment</div>
            <div className="fs-5" data-testid="sub-last-payment">
              {sub.last_payment_status === 'succeeded' && <span className="text-success">Succeeded</span>}
              {sub.last_payment_status === 'failed' && <span className="text-danger">Failed</span>}
              {!sub.last_payment_status && <span className="text-muted">—</span>}
            </div>
          </Col>
        </Row>
        {status === 'past_due' && (
          <Alert variant="warning" className="mt-3 mb-0" data-testid="past-due-alert">
            <i className="bi bi-exclamation-triangle-fill me-2" />
            Your subscription is past due. Please update your payment method to avoid interruption.
          </Alert>
        )}
      </Card.Body>
      <Card.Footer className="d-flex justify-content-between align-items-center">
        <small className="text-muted">
          Subscription id: <code>{(sub.stripe_subscription_id || sub.id || '').slice(-16)}</code>
        </small>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" size="sm" onClick={onRefresh} data-testid="refresh-sub-btn">
            <i className="bi bi-arrow-clockwise me-1" />Refresh
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={openPortal}
            disabled={!hasStripeCustomer || portalLoading}
            title={hasStripeCustomer ? 'Manage payment method, invoices, and cancellation' : 'No Stripe customer on file'}
            data-testid="open-portal-btn"
          >
            {portalLoading && <Spinner size="sm" className="me-1" />}
            <i className="bi bi-box-arrow-up-right me-1" />
            Manage in Stripe
          </Button>
        </div>
      </Card.Footer>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Assignment requests card
// ---------------------------------------------------------------------------
function AssignmentRequestsCard({ toast }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.get('/me/assignment-requests');
      setItems(r.data?.data || []);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const statusVariants = useMemo(() => ({
    pending: 'warning',
    approved: 'success',
    rejected: 'danger',
  }), []);

  return (
    <Card data-testid="assignment-requests-card">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <div>
          <div className="fw-semibold">Candidate assignment requests</div>
          <small className="text-muted">Request access to manage a specific candidate profile.</small>
        </div>
        <Button variant="primary" size="sm" onClick={() => setShowModal(true)} data-testid="new-request-btn">
          <i className="bi bi-plus-lg me-1" />New request
        </Button>
      </Card.Header>
      <Card.Body className="p-0">
        {loading ? (
          <div className="text-center py-4"><Spinner animation="border" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-5 text-muted" data-testid="empty-requests">
            <i className="bi bi-inbox" style={{ fontSize: '2rem' }} />
            <div className="mt-2">No requests yet.</div>
            <small>Click &quot;New request&quot; to ask for candidate access.</small>
          </div>
        ) : (
          <Table hover responsive className="mb-0" size="sm" data-testid="requests-table">
            <thead>
              <tr>
                <th>Candidate</th>
                <th>Note</th>
                <th>Status</th>
                <th>Submitted</th>
              </tr>
            </thead>
            <tbody>
              {items.map((x) => (
                <tr key={x.id} data-testid={`request-row-${x.id}`}>
                  <td>
                    {x.candidate_full_name ? (
                      <div>
                        <div className="fw-semibold">{x.candidate_full_name}</div>
                        <small className="text-muted">{x.candidate_slug}</small>
                      </div>
                    ) : x.candidate_id ? (
                      <code className="small">{x.candidate_id.slice(0, 8)}…</code>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td className="small" style={{ maxWidth: 320 }}>{x.candidate_note || <span className="text-muted">—</span>}</td>
                  <td>
                    <Badge bg={statusVariants[x.status] || 'secondary'}>
                      {x.status || 'unknown'}
                    </Badge>
                  </td>
                  <td className="small text-muted">
                    {x.created_at ? new Date(x.created_at).toLocaleDateString() : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>
      {showModal && (
        <NewRequestModal
          onClose={() => setShowModal(false)}
          onCreated={() => { setShowModal(false); load(); }}
          toast={toast}
        />
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// New request modal
// ---------------------------------------------------------------------------
function NewRequestModal({ onClose, onCreated, toast }) {
  const [candidateId, setCandidateId] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [candidateResults, setCandidateResults] = useState([]);
  const [search, setSearch] = useState('');
  const [searching, setSearching] = useState(false);

  // Debounced live search across candidates by name/slug/office.
  useEffect(() => {
    if (!search || search.length < 2) { setCandidateResults([]); return undefined; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await adminApi.get('/admin/candidates', { params: { q: search, page_size: 8 } });
        setCandidateResults(r.data?.items || []);
      } catch {
        setCandidateResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const submit = async () => {
    if (!candidateId && !note.trim()) {
      toast.error('Pick a candidate or enter a note.');
      return;
    }
    setSaving(true);
    try {
      await adminApi.post('/me/assignment-requests', {
        candidate_id: candidateId || null,
        candidate_note: note.trim(),
      });
      toast.success('Request submitted. An admin will review shortly.');
      onCreated();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show onHide={onClose} centered data-testid="new-request-modal">
      <Modal.Header closeButton>
        <Modal.Title>Request candidate assignment</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Label>Search candidate (optional)</Form.Label>
        <InputGroup className="mb-2">
          <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
          <Form.Control
            placeholder="Type at least 2 characters…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setCandidateId(''); }}
            data-testid="request-search-input"
          />
        </InputGroup>
        {searching && <div className="text-muted small mb-2"><Spinner size="sm" /> Searching…</div>}
        {candidateResults.length > 0 && (
          <div className="border rounded mb-3" style={{ maxHeight: 200, overflowY: 'auto' }} data-testid="request-search-results">
            {candidateResults.map((c) => (
              <button
                key={c.id}
                type="button"
                className={`btn btn-link text-start w-100 border-bottom py-2 px-3 text-decoration-none ${candidateId === c.id ? 'bg-light' : ''}`}
                onClick={() => { setCandidateId(c.id); setSearch(c.full_name); setCandidateResults([]); }}
                data-testid={`request-search-pick-${c.id}`}
              >
                <div className="fw-semibold text-dark">{c.full_name}</div>
                <small className="text-muted">{c.slug} · {c.party || 'No party'}</small>
              </button>
            ))}
          </div>
        )}
        {candidateId && (
          <Alert variant="info" className="py-2 small mb-3" data-testid="picked-candidate-alert">
            <i className="bi bi-person-check me-1" />
            Requesting assignment to: <strong>{search}</strong>
            <Button
              variant="link"
              size="sm"
              className="p-0 ms-2"
              onClick={() => { setCandidateId(''); setSearch(''); }}
              data-testid="clear-picked-candidate"
            >
              clear
            </Button>
          </Alert>
        )}
        <Form.Label>Note to admin {candidateId ? '(optional)' : ''}</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          placeholder={candidateId ? 'Any extra context (optional)…' : 'Describe the candidate (name, office, district)…'}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          data-testid="request-note-input"
        />
        <div className="text-muted small mt-2">
          <i className="bi bi-info-circle me-1" />
          An admin will review your request and, once approved, you&apos;ll be able to edit the candidate&apos;s profile from your dashboard.
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={saving} data-testid="cancel-request-btn">Cancel</Button>
        <Button variant="primary" onClick={submit} disabled={saving} data-testid="submit-request-btn">
          {saving && <Spinner size="sm" className="me-1" />}
          Submit request
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
